import type { JSXElement, VoidComponent } from "solid-js";
import { createEffect, createSignal, getOwner, Match, runWithOwner, Show, splitProps, Switch, untrack } from "solid-js";
import { IconButton, TextButton } from "../button/Button";
import { Dialog } from "@ark-ui/solid";
import { Icon } from "../icon/Icon";
import { omit } from "lodash-es";
import InlineMessage from "../inlineMessage/InlineMessage";
import { createTimer } from "@solid-primitives/timer";
import { SLOW_SUBMIT_DELAY } from "../../constants";
import logger from "../../utils/logger";
import { useCurrentModal } from "./ModalManager";
import Text from "../text/Text.tsx";

type GenericModalProps = {
  title: string;
  description: string | JSXElement;
  onConfirm?: (() => Promise<void>) | (() => void);
};

type WithInlineMessage = {
  inlineMessage?: string | JSXElement;
};

type ModalProps = {
  variant:
    | { type: "success"; confirmLabel: string; cancelLabel: string }
    | ({ type: "warning"; confirmLabel: string; cancelLabel: string } & WithInlineMessage)
    | ({ type: "informative"; confirmLabel: string; closeAriaLabel: string } & WithInlineMessage)
    | ({
        type: "destructive";
        confirmLabel: string;
        confirmLabelSlow?: string;
        cancelLabel: string;
      } & WithInlineMessage);
} & GenericModalProps;

const Modal: VoidComponent<ModalProps> = (props) => {
  const { close, changeCloseBehaviour, isClosing } = useCurrentModal();

  const owner = getOwner();
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [isSubmittingSlow, setIsSubmittingSlow] = createSignal(false);
  createTimer(
    () => {
      setIsSubmittingSlow(true);
    },
    () => isSubmitting() && SLOW_SUBMIT_DELAY,
    setTimeout,
  );

  createEffect(() => {
    if (isSubmitting()) {
      changeCloseBehaviour("off");
    } else if (props.variant.type === "warning" || props.variant.type === "destructive") {
      changeCloseBehaviour("actions_only");
    } else {
      changeCloseBehaviour("default");
    }
  });

  const submit = async () => {
    try {
      setIsSubmitting(true);
      const submit = untrack(() => props.onConfirm);

      await runWithOwner(owner, async () => {
        await submit?.();
      });

      close();
    } catch (e) {
      logger.warn("Failed to submit", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showCloseButton = props.variant.type === "informative";

  return (
    <div class="flex w-86 flex-col gap-5 p-6 shadow-elevation-light-5">
      <div class="flex flex-col gap-2">
        <Show when={showCloseButton}>
          <div class="absolute top-3 right-3">
            <Dialog.CloseTrigger
              asChild={(parentProps) => (
                <IconButton
                  {...omit(parentProps(), "children")}
                  icon="x"
                  aria-label={getCloseAriaLabel(props)!}
                  size="large"
                  variant="tertiary"
                />
              )}
            />
          </div>
        </Show>
        <Dialog.Title class="flex items-center gap-2" classList={{ "mr-6": showCloseButton }}>
          <div
            class="flex size-9 flex-none shrink-0 items-center justify-center rounded-full"
            classList={{
              "bg-surface-status-subtle-danger text-status-danger [&>svg]:icon-status-danger":
                props.variant.type === "destructive",
              "bg-surface-status-subtle-warning text-status-warning [&>svg]:icon-status-warning":
                props.variant.type === "warning" || props.variant.type === "informative",
            }}
          >
            <Switch>
              <Match when={props.variant.type === "success"}>
                <Icon name="outlineCheckCircle" size="xl" type="outline" />
              </Match>
              <Match when={props.variant.type === "warning" || props.variant.type === "informative"}>
                <Icon name="outlineExclamation" size="xl" type="outline" />
              </Match>
              <Match when={props.variant.type === "destructive"}>
                <Icon name="outlineExclamationCircle" size="xl" type="outline" />
              </Match>
            </Switch>
          </div>
          <Text text={props.title} truncate={2} class="text-header-lg text-primary" />
        </Dialog.Title>
        <Dialog.Description class="max-h-122.5 overflow-y-auto">
          <Show when={props.description}>
            {(description) => (
              <Text as="p" text={description()} truncate={4} class="text-base-regular text-secondary" />
            )}
          </Show>
          <Switch>
            <Match
              when={
                (props.variant.type === "warning" || props.variant.type === "informative") &&
                props.variant.inlineMessage
              }
            >
              <div class="mt-2">
                <InlineMessage variant="warning">{getInlineMessage(props)!}</InlineMessage>
              </div>
            </Match>
            <Match when={props.variant.type === "destructive" && props.variant.inlineMessage}>
              <div class="mt-2">
                <InlineMessage variant="danger">{getInlineMessage(props)!}</InlineMessage>
              </div>
            </Match>
          </Switch>
        </Dialog.Description>
      </div>
      <div class="flex gap-2">
        <Show when={props.variant.type !== "informative"}>
          <Dialog.CloseTrigger
            asChild={(parentProps) => (
              <TextButton
                {...omit(parentProps(), "children")}
                text={getCancelLabel(props)!}
                disabled={isSubmitting() || isClosing()}
                variant="secondary"
                block
                size="large"
              />
            )}
          />
        </Show>
        <TextButton
          text={
            props.variant.type === "destructive" && isSubmittingSlow()
              ? (props.variant.confirmLabelSlow ?? props.variant.confirmLabel)
              : props.variant.confirmLabel
          }
          loading={isSubmitting()}
          disabled={isSubmitting() || isClosing()}
          variant="primary"
          color={props.variant.type === "destructive" ? "destructive" : undefined}
          block
          size="large"
          onClick={() => submit()}
        />
      </div>
    </div>
  );
};

const getCancelLabel = (properties: ModalProps) =>
  properties.variant.type !== "informative" ? properties.variant.cancelLabel : undefined;

const getCloseAriaLabel = (properties: ModalProps) =>
  properties.variant.type === "informative" ? properties.variant.closeAriaLabel : undefined;

const getInlineMessage = (properties: ModalProps) =>
  properties.variant.type === "warning" ||
  properties.variant.type === "destructive" ||
  properties.variant.type === "informative"
    ? properties.variant.inlineMessage
    : undefined;

export type DestructiveModalProps = {
  confirmLabel: string;
  confirmLabelSlow?: string;
  cancelLabel: string;
} & WithInlineMessage &
  GenericModalProps;

export const DestructiveModal: VoidComponent<DestructiveModalProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, [
    "inlineMessage",
    "confirmLabel",
    "confirmLabelSlow",
    "cancelLabel",
  ]);

  return (
    <Modal
      variant={{
        type: "destructive",
        ...localProps,
      }}
      {...otherProps}
    />
  );
};

export type WarningModalProps = {
  confirmLabel: string;
  cancelLabel: string;
} & WithInlineMessage &
  GenericModalProps;

export const WarningModal: VoidComponent<WarningModalProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["inlineMessage", "confirmLabel", "cancelLabel"]);
  return (
    <Modal
      variant={{
        type: "warning",
        ...localProps,
      }}
      {...otherProps}
    />
  );
};

export type InformativeModalProps = {
  confirmLabel: string;
  closeAriaLabel: string;
} & WithInlineMessage &
  GenericModalProps;

export const InformativeModal: VoidComponent<InformativeModalProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["inlineMessage", "confirmLabel", "closeAriaLabel"]);
  return (
    <Modal
      variant={{
        type: "informative",
        ...localProps,
      }}
      {...otherProps}
    />
  );
};
